/**
 * Compact card showing Delivery vs Dine-in split by revenue (with %).
 * Pick-up is shown as a subtle third row when it has any revenue.
 * Each bucket exposes a WoW/MoM delta so the same component works in
 * Sales (current period) and Growth (trend) contexts.
 */
import { Bike, ChefHat, ShoppingBag } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from './EmptyState';
import { formatINRCompact } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { SplitBucket } from '@/hooks/useChannelSplit';

interface Props {
  delivery: SplitBucket;
  dineIn: SplitBucket;
  pickUp: SplitBucket;
  totalSales: number;
  loading?: boolean;
  compact?: boolean;
  showDelta?: boolean;
}

const BUCKET_META = {
  Delivery: { icon: Bike, color: 'text-orange-500', bg: 'bg-orange-500' },
  'Dine-in': { icon: ChefHat, color: 'text-emerald-500', bg: 'bg-emerald-500' },
  'Pick-up': { icon: ShoppingBag, color: 'text-sky-500', bg: 'bg-sky-500' },
} as const;

export function DeliveryDineinSplit({
  delivery,
  dineIn,
  pickUp,
  totalSales,
  loading,
  compact,
  showDelta = true,
}: Props) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (totalSales === 0) {
    return <EmptyState title="No sales yet" description="No orders in this period." />;
  }

  const buckets: SplitBucket[] = [delivery, dineIn];
  if (pickUp.sales > 0) buckets.push(pickUp);

  return (
    <div className="space-y-2">
      {/* Combined bar */}
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
        {buckets.map((b) => {
          const meta = BUCKET_META[b.label];
          return (
            <div
              key={b.label}
              className={cn('h-full transition-all', meta.bg)}
              style={{ width: `${b.sharePct}%` }}
              title={`${b.label}: ${formatINRCompact(b.sales)} (${b.sharePct.toFixed(1)}%)`}
            />
          );
        })}
      </div>

      {/* Rows */}
      <div className={cn('grid gap-1.5', compact ? 'grid-cols-2' : 'grid-cols-1')}>
        {buckets.map((b) => {
          const meta = BUCKET_META[b.label];
          const Icon = meta.icon;
          return (
            <div
              key={b.label}
              className="flex items-center justify-between rounded-md border border-border/40 bg-card/60 px-2.5 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Icon className={cn('h-3.5 w-3.5 shrink-0', meta.color)} />
                <div className="min-w-0">
                  <div className="text-xs font-medium leading-tight">{b.label}</div>
                  {!compact && b.channels.length > 0 && (
                    <div className="text-2xs text-muted-foreground truncate">
                      {b.channels.slice(0, 3).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold tabular-nums">
                  {formatINRCompact(b.sales)}
                </div>
                <div className="flex items-center justify-end gap-1 text-2xs tabular-nums">
                  <span className="text-muted-foreground">{b.sharePct.toFixed(1)}%</span>
                  {showDelta && b.deltaPct != null && Math.abs(b.deltaPct) >= 0.5 && (
                    <span
                      className={cn(
                        b.deltaPct > 0 ? 'text-emerald-500' : 'text-rose-500',
                      )}
                    >
                      {b.deltaPct > 0 ? '↑' : '↓'}
                      {Math.abs(b.deltaPct).toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
